import type { ReactNode } from "react";

export type UserStatus = "full" | "invited" | "impersonateOnly" | "none";

/* User-status icon system (64-grid, 3px rounded stroke) ported from the prototype.
 * none / impersonateOnly render muted (limited account). */
const GLYPHS: Record<UserStatus, ReactNode> = {
  full: (
    <>
      <circle cx="32.53" cy="27.04" r="7.21" />
      <path d="M19.3 48.73C20.42 42.74 25.68 38.2 32 38.2s11.58 4.54 12.7 10.53" />
      <circle cx="32" cy="32" r="21" />
    </>
  ),
  invited: (
    <>
      <circle cx="32.53" cy="28.36" r="6.23" />
      <path d="M20.5 49.59c1.02-5.42 5.78-9.53 11.5-9.53s10.48 4.11 11.5 9.53" />
      <path d="M32 11c-11.6 0-21 9.4-21 21s9.4 21 21 21 21-9.4 21-21" />
      <rect x="42.62" y="9.81" width="15.11" height="10.18" rx="1.08" ry="1.08" />
      <path d="M42.62 12.18l6.93 4.84c.37.26.87.26 1.24 0l6.93-4.84" />
    </>
  ),
  impersonateOnly: (
    <>
      <path d="M39.59 31.19c.03.27.04.53.04.81 0 4.03-3.27 7.31-7.31 7.31s-7.31-3.27-7.31-7.31 3.27-7.31 7.31-7.31c2.02 0 3.84.82 5.17 2.14" />
      <path d="M10.44 32c.35-.57 7.82-12.3 21.89-12.11.42 0 .84.02 1.25.05 3.79.24 7.44 1.5 10.71 3.43l.72.42c1.13.66 2.23 1.39 3.23 2.24 3.33 2.82 5.11 5.63 5.32 5.98" />
      <path d="M53.56 32c-.35.57-7.82 12.3-21.89 12.11h-.04c-2.12-.03-4.23-.43-6.26-1.07l-1.18-.37c-1.74-.54-3.44-1.26-4.99-2.21-5.54-3.4-8.47-8.01-8.75-8.46" />
    </>
  ),
  none: (
    <>
      <circle cx="32.53" cy="27.04" r="7.21" />
      <path d="M19.3 48.73C20.42 42.74 25.68 38.2 32 38.2s11.58 4.54 12.7 10.53" />
      <circle cx="32" cy="32" r="21" />
      <line x1="17.16" y1="17.16" x2="46.84" y2="46.84" />
    </>
  ),
};

type Props = {
  status?: UserStatus;
  size?: number;
  title?: string;
};

export function UserStatusIcon({ status = "full", size = 34, title }: Props) {
  const muted = status === "none" || status === "impersonateOnly";
  const color = muted ? "var(--color-grey-lighter)" : "var(--color-grey-dark)";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, display: "block" }}
      data-status={status}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {GLYPHS[status]}
    </svg>
  );
}
